import { onMounted, ref, watch, type Ref } from 'vue'

import { reportError } from '@/utils/report-error.util.js'

const STORAGE_KEY = 'zapio.nav.collapsed'

/**
 * Whether the section nav is collapsed to just the icon rail.
 *
 * **Browser-local on purpose.** This is panel chrome, not merchant state: it belongs to
 * this operator at this screen size, it is worth nothing on another device, and there is
 * no operator-preference endpoint to put it behind. (The rule that UI state belongs on the
 * backend is about a *shop's* state, which several services read — not about whether one
 * person likes a narrow sidebar.)
 *
 * Read in `onMounted` rather than at module scope: `localStorage` throws in a sandboxed
 * frame and is absent under SSR or a unit test's jsdom reset, and a composable that
 * explodes on import takes the whole shell with it.
 */
export function useNavCollapse(): { collapsed: Ref<boolean>; toggle: () => void } {
  const collapsed = ref(false)

  onMounted(() => {
    try {
      collapsed.value = globalThis.localStorage.getItem(STORAGE_KEY) === '1'
    } catch (error) {
      reportError('nav-collapse:read', error)
    }
  })

  watch(collapsed, (value) => {
    try {
      globalThis.localStorage.setItem(STORAGE_KEY, value ? '1' : '0')
    } catch (error) {
      // A preference that cannot be saved is a preference that resets, never a crash.
      reportError('nav-collapse:write', error)
    }
  })

  return {
    collapsed,
    toggle: (): void => {
      collapsed.value = !collapsed.value
    },
  }
}
